/**
 * Typed client for the two-scale feedback UI (per-message thumbs/notes —
 * message-reaction-row.tsx — and the whole-conversation rating card —
 * conversation-rating-card.tsx) — `/api/conversations/{conversationId}/feedback`.
 *
 * The three enums below are re-exported from the generated contract (same
 * pattern as support-requests.ts and src/types/conversation.ts) — their
 * string values are verified byte-identical to what used to be hand-written
 * here, so that part of the swap is a pure, zero-risk consolidation. Do NOT
 * hand-edit them again; change `aol_schemas.feedback` (aol-us-shared) and
 * regenerate with `./devops build types`.
 *
 * `FeedbackPayload`/`FeedbackResponse` are now the generated
 * `FeedbackCreate`/`FeedbackResponse` DTOs (components in feedback-dto.d.ts).
 * A previous attempt left these hand-written because every optional-with-a-
 * default field (message_id, handoff_id, sentiment, rating, comment, source)
 * generated as a REQUIRED key typed `T | null`, not an omittable optional key
 * — the opposite of how every call site here builds these objects (partial
 * object literals relying on the omitted keys being genuinely optional).
 * That was a codegen flag defect, not a schema decision: the generator now
 * runs with `--default-non-nullable false` for this spec, which emits a
 * Pydantic field-with-a-default as `field?: T | null` instead of
 * required-but-nullable — matching what "has a default" actually means (omit
 * it, get the default). With that fixed upstream, this is a pure
 * type-identity swap.
 *
 * Non-2xx bodies are normalised via `extractErrorMessage` (./http-error) —
 * shared across every REST caller against this backend; see that module's
 * own doc comment for why a FastAPI 422's `detail` array needs normalising
 * at all (it used to render the literal text "[object Object]").
 */
import { getAssistantApiUrl } from '@/lib/assistant-config';
import { getDeviceToken } from '@/lib/device-token';
import {
  FeedbackScope,
  FeedbackSentiment,
  FeedbackSource,
} from '@/types/generated/feedback-contract';
import type { components } from '@/types/generated/feedback-dto';
import { extractErrorMessage } from './http-error';

export { FeedbackScope, FeedbackSentiment };

/** Where the conversation-scoped ask came from (message-scoped feedback has
 *  no source — a thumb only ever comes from the reaction row itself). Drives
 *  the once-per-conversation gate in conversation-feedback-ask.ts, which
 *  imports this type rather than redeclaring the same four strings. */
export { FeedbackSource };

/** Request body for POST .../feedback. `message_id`/`handoff_id` are only
 *  meaningful for scope MESSAGE; `rating` only for scope CONVERSATION — left
 *  as a flat shape (not a discriminated union) to mirror
 *  support-requests.ts's SupportRequestPayload, which takes the same "one
 *  flat shape, backend enforces which fields matter per variant" approach.
 *  `source`, unlike the others, is required on every submission regardless of
 *  scope — both a message-row thumb and a conversation rating must say where
 *  they came from (the NOT NULL column has no "no source" state); see
 *  FeedbackSource's doc comment for the full vocabulary. `sentiment` omitted
 *  entirely (not sent as null) represents a retraction of a previously-given
 *  thumb — the wire type allows this since the field is optional; see
 *  message-reaction-row.tsx's retraction path.
 *
 *  Now the generated `FeedbackCreate` DTO — see this file's header for why
 *  the earlier attempt at this swap was blocked and what fixed it. */
export type FeedbackPayload = components['schemas']['FeedbackCreate'];

/** 200 response shape. `id` lets a caller correlate a later GET row back to
 *  the POST that created/updated it.
 *
 *  Now the generated `FeedbackResponse` DTO — see this file's header. Two
 *  fields are stricter than the old hand-written shape: `source` is required
 *  (every persisted row has one, not just conversation-scoped ones) and
 *  `updated_at` is present at all (the hand-written type omitted it
 *  entirely — an incompleteness this swap corrects, not a backend behavior
 *  change: the API has always returned it). */
export type FeedbackResponse = components['schemas']['FeedbackResponse'];

/**
 * Submit feedback (a message thumb/note, or a conversation rating). Throws
 * on any non-2xx response or network failure — callers are expected to catch
 * this and surface the message inline without discarding the user's typed
 * content, same contract as support-requests.ts's submitSupportRequest.
 */
export async function submitFeedback(
  conversationId: string,
  payload: FeedbackPayload
): Promise<FeedbackResponse> {
  const deviceToken = getDeviceToken();
  const response = await fetch(
    getAssistantApiUrl(`/api/conversations/${conversationId}/feedback`),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Device-Token': deviceToken,
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    const error: unknown = await response.json().catch(() => ({}));
    const fallback = `Feedback submission failed: ${response.status} ${response.statusText}`;
    throw new Error(extractErrorMessage(error, fallback));
  }

  return response.json();
}

/**
 * Undo a message's feedback — the user tapped an already-active thumb.
 *
 * A DELETE, not a submission with sentiment omitted: the wire contract
 * requires message-scope feedback to carry at least one of sentiment or
 * comment, so a bare retraction ("remove my thumb, I said nothing else")
 * has no valid request body and would be rejected outright. That validator
 * is deliberate — it stops an empty submission being recorded as signal —
 * so undo gets its own verb instead of weakening it.
 *
 * Idempotent server-side (204 whether or not a row was there), so a
 * double-tap or a retry can't fail.
 */
export async function retractMessageFeedback(
  conversationId: string,
  messageId: string
): Promise<void> {
  const deviceToken = getDeviceToken();
  const response = await fetch(
    getAssistantApiUrl(
      `/api/conversations/${conversationId}/feedback/message/${messageId}`
    ),
    {
      method: 'DELETE',
      headers: {
        'X-Device-Token': deviceToken,
      },
    }
  );

  if (!response.ok) {
    const error: unknown = await response.json().catch(() => ({}));
    const fallback = `Retracting feedback failed: ${response.status} ${response.statusText}`;
    throw new Error(extractErrorMessage(error, fallback));
  }
}

/**
 * Fetch every feedback row recorded for a conversation so far — used on
 * conversation load to restore per-message thumb state (a message the user
 * already rated must show its thumb after a refresh, not a blank row; see
 * lib/feedback-restore.ts for the pure map-building helper consumers use on
 * this result).
 */
export async function getConversationFeedback(
  conversationId: string
): Promise<FeedbackResponse[]> {
  const deviceToken = getDeviceToken();
  const response = await fetch(
    getAssistantApiUrl(`/api/conversations/${conversationId}/feedback`),
    {
      method: 'GET',
      headers: {
        'X-Device-Token': deviceToken,
      },
    }
  );

  if (!response.ok) {
    const error: unknown = await response.json().catch(() => ({}));
    const fallback = `Fetching feedback failed: ${response.status} ${response.statusText}`;
    throw new Error(extractErrorMessage(error, fallback));
  }

  return response.json();
}
